import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, X, Smartphone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function PWAInstallBanner() {
  const { data: siteSettings = {} } = useQuery<Record<string, string>>({
    queryKey: ["/api/settings/site"],
    queryFn: () => fetch("/api/settings/site").then(r => r.ok ? r.json() : {}),
  });

  const bannerEnabled  = siteSettings.pwa_install_banner_enabled !== "false";
  const bannerTitle    = siteSettings.pwa_banner_title       || "Add to Home Screen";
  const bannerSubtitle = siteSettings.pwa_banner_subtitle    || "Install Eliada Sports Agency as an app for quick access";
  const bannerButton   = siteSettings.pwa_banner_button_text || "Install";

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible]   = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Never show if already running as an installed app
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setVisible(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => setVisible(false);

  if (installed || !bannerEnabled) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="pwa-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 32 }}
          className="fixed bottom-0 left-0 right-0 z-[9990] px-4 pb-6 pointer-events-none"
        >
          <div className="max-w-lg mx-auto bg-[#0b1526] border border-primary/30 rounded-2xl shadow-2xl shadow-black/50 p-4 flex items-center gap-4 pointer-events-auto">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <Smartphone size={22} className="text-black" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm leading-tight">{bannerTitle}</p>
              <p className="text-white/50 text-xs mt-0.5 leading-snug">{bannerSubtitle}</p>
            </div>

            <div className="shrink-0 flex items-center gap-2">
              <button
                onClick={handleInstall}
                className="flex items-center gap-1.5 bg-primary text-black font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-xl hover:bg-primary/90 active:scale-95 transition-all"
              >
                <Download size={13} />
                {bannerButton}
              </button>
              <button
                onClick={handleDismiss}
                aria-label="Dismiss install prompt"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
